import { Element, type Node } from 'slate';
import type {
  AudioElement,
  CodeElement,
  CodeSnippetElement,
  DividerLineElement,
  EmbeddedWhiteboardElement,
  EmbedElement,
  Heading1Element,
  Heading2Element,
  Heading3Element,
  Heading4Element,
  Heading5Element,
  Heading6Element,
  ImageElement,
  InfoBlockElement,
  LabelPillElement,
  LinkElement,
  LinkCardElement,
  ParagraphElement,
  QuoteElement,
  SharedSnippetElement,
  SharedSnippetInlineElement,
  TableCellElement,
  TableElement,
  TableRowElement,
  VideoElement,
  AttachmentElement,
  AnnotationKey,
  TranscriptCueElement,
  TranscriptSpeakerElement,
  EmailVariableReferenceElement,
  EmbeddedReportElement,
  EmbeddedReportPlaceholderElement,
  AhaReferenceElement,
  UserReferenceElement,
} from './aha-editor';
import { LOCAL_ONLY_ANNOTATION_KEY } from '../plugins/AnnotationsPlugin/utils';

export const isElementFactory = <T extends Element>(type: string) => {
  return (element: Node): element is T =>
    Element.isElement(element) && element.type === type;
};

export const isParagraphElement =
  isElementFactory<ParagraphElement>('paragraph');
export const isCodeBlockElement = isElementFactory<CodeElement>('code');
export const isHeading1Element = isElementFactory<Heading1Element>('h1');
export const isHeading2Element = isElementFactory<Heading2Element>('h2');
export const isHeading3Element = isElementFactory<Heading3Element>('h3');
export const isHeading4Element = isElementFactory<Heading4Element>('h4');
export const isHeading5Element = isElementFactory<Heading5Element>('h5');
export const isHeading6Element = isElementFactory<Heading6Element>('h6');
export const isQuoteElement = isElementFactory<QuoteElement>('quote');

export const isCommonBlockElement = (
  element: Node
): element is
  | ParagraphElement
  | CodeElement
  | Heading1Element
  | Heading2Element
  | Heading3Element
  | Heading4Element
  | Heading5Element
  | Heading6Element
  | QuoteElement => {
  return (
    isParagraphElement(element) ||
    isCodeBlockElement(element) ||
    isHeading1Element(element) ||
    isHeading2Element(element) ||
    isHeading3Element(element) ||
    isHeading4Element(element) ||
    isHeading5Element(element) ||
    isHeading6Element(element) ||
    isQuoteElement(element)
  );
};

export const isAhaReferenceElement =
  isElementFactory<AhaReferenceElement>('aha-reference');
export const isUserReferenceElement =
  isElementFactory<UserReferenceElement>('user-reference');
export const isLinkElement = isElementFactory<LinkElement>('link');
export const isLinkCardElement = isElementFactory<LinkCardElement>('linkCard');

export const isTableCellElement =
  isElementFactory<TableCellElement>('table-cell');

export const isTableRowElement = isElementFactory<TableRowElement>('table-row');

export const isTableElement = isElementFactory<TableElement>('table');

export const isAudioElement = isElementFactory<AudioElement>('audio');
export const isImageElement = isElementFactory<ImageElement>('image');
export const isVideoElement = isElementFactory<VideoElement>('video');
export const isAttachmentElement =
  isElementFactory<AttachmentElement>('attachment');

export const isSharedSnippetElement = (
  element: Node
): element is SharedSnippetElement | SharedSnippetInlineElement =>
  Element.isElement(element) &&
  ['shared-snippet', 'shared-snippet-inline'].includes(element.type);

export const isEmbedElement = isElementFactory<EmbedElement>('embed');

export const isArtifactEmbedElement = (
  element: Node
): element is EmbedElement =>
  isEmbedElement(element) &&
  !!element.embed_src?.match(/assistant\/shared_artifacts/);

export const isLabelPillElement = isElementFactory<LabelPillElement>('pill');

export const isDividerLineElement =
  isElementFactory<DividerLineElement>('divider-line');

export const isCodeSnippetElement =
  isElementFactory<CodeSnippetElement>('code-snippet');

export const isInlineParagraphElement =
  isElementFactory<CodeSnippetElement>('inline-paragraph');

export const isEmbeddedWhiteboardElement =
  isElementFactory<EmbeddedWhiteboardElement>('embedded-whiteboard');

export const isEmbeddedReportPlaceholderElement =
  isElementFactory<EmbeddedReportPlaceholderElement>(
    'embedded-report-placeholder'
  );

export const isEmbeddedReportElement =
  isElementFactory<EmbeddedReportElement>('embedded-report');

export const isMediaElement = (
  element: Node
): element is
  | AudioElement
  | ImageElement
  | VideoElement
  | AttachmentElement => {
  return (
    isAudioElement(element) ||
    isImageElement(element) ||
    isVideoElement(element) ||
    isAttachmentElement(element)
  );
};

export const isResizableElement = (
  element: Node
): element is ImageElement | VideoElement | EmbedElement => {
  return (
    isImageElement(element) ||
    isVideoElement(element) ||
    isEmbedElement(element)
  );
};

export const isInfoBlockElement = (
  element: Node
): element is InfoBlockElement =>
  Element.isElement(element) &&
  ['infoBlock', 'noteBlock', 'warningBlock', 'tipBlock'].includes(element.type);

export const isAnnotationKey = (key: string): key is AnnotationKey =>
  !!key.match('\\d+') || key === LOCAL_ONLY_ANNOTATION_KEY;

export const isTranscriptSpeakerElement =
  isElementFactory<TranscriptSpeakerElement>('transcript-speaker');

export const isTranscriptCueElement =
  isElementFactory<TranscriptCueElement>('transcript-cue');

export const isEmailVariableReferenceElement =
  isElementFactory<EmailVariableReferenceElement>('email-variable-reference');
